const clamp = (value, minimum, maximum) => Math.max(minimum, Math.min(maximum, value));

export const canvasControlMinimumSize = (kind, componentVariant = null) => {
  const compactIndicator = kind === "indicator" && (!componentVariant || componentVariant === "round-indicator");
  if (kind === "keyboard") return { width: 240, height: 112 };
  if (compactIndicator) return { width: 12, height: 12 };
  if (kind === "text") return { width: 32, height: 24 };
  return { width: 48, height: 48 };
};

export const panelContentClientBounds = (bounds, headerHeight) => ({
  x: bounds.x,
  y: bounds.y + headerHeight,
  left: bounds.left,
  top: bounds.top + headerHeight,
  right: bounds.right,
  bottom: bounds.bottom,
  width: bounds.width,
  height: Math.max(1, bounds.height - headerHeight),
});

export const resizeCanvasControlWithinBounds = ({
  kind,
  componentVariant,
  preserveAspectRatio = true,
  width: initialWidth,
  height: initialHeight,
  x,
  y,
  boundsWidth,
  boundsHeight,
  deltaX,
  deltaY,
}) => {
  const { width: minimumWidth, height: minimumHeight } = canvasControlMinimumSize(kind, componentVariant);
  const horizontalInset = 8;
  const topInset = 36;
  const bottomInset = 8;
  const originalLeft = ((x / 100) * boundsWidth) - (initialWidth / 2);
  const originalTop = ((y / 100) * boundsHeight) - (initialHeight / 2);

  // A centered keyboard often starts close to the right edge. Let it keep
  // growing by shifting its left edge once the right edge reaches the canvas.
  const maximumWidth = Math.max(minimumWidth, Math.min(1600, boundsWidth - (horizontalInset * 2)));
  const top = Math.max(topInset, originalTop);
  const maximumHeight = Math.max(minimumHeight, Math.min(1024, boundsHeight - top - bottomInset));
  let width;
  let height;
  if (preserveAspectRatio) {
    const horizontalScaleDelta = deltaX / Math.max(1, initialWidth);
    const verticalScaleDelta = deltaY / Math.max(1, initialHeight);
    const requestedScale = 1 + (
      Math.abs(horizontalScaleDelta) >= Math.abs(verticalScaleDelta)
        ? horizontalScaleDelta
        : verticalScaleDelta
    );
    const minimumScale = Math.max(minimumWidth / initialWidth, minimumHeight / initialHeight);
    const maximumScale = Math.min(maximumWidth / initialWidth, maximumHeight / initialHeight);
    const scale = clamp(requestedScale, minimumScale, maximumScale);
    width = Math.round(initialWidth * scale);
    height = Math.round(initialHeight * scale);
  } else {
    width = Math.round(clamp(initialWidth + deltaX, minimumWidth, maximumWidth));
    height = Math.round(clamp(initialHeight + deltaY, minimumHeight, maximumHeight));
  }
  const maximumLeft = Math.max(horizontalInset, boundsWidth - width - horizontalInset);
  const left = clamp(originalLeft, horizontalInset, maximumLeft);

  return {
    width,
    height,
    x: Math.round(((left + (width / 2)) / boundsWidth) * 100),
    y: Math.round(((top + (height / 2)) / boundsHeight) * 100),
  };
};

export const offsetCanvasControlGroupWithinBounds = ({
  controls,
  boundsWidth,
  boundsHeight,
  offsetX,
  offsetY,
}) => {
  if (!controls.length) return [];

  const horizontalLimits = controls.map((control) => {
    const halfWidth = Math.min(50, (control.width / boundsWidth) * 50);
    return { minimum: halfWidth - control.x, maximum: (100 - halfWidth) - control.x };
  });
  const verticalLimits = controls.map((control) => {
    const halfHeight = Math.min(50, (control.height / boundsHeight) * 50);
    return { minimum: halfHeight - control.y, maximum: (100 - halfHeight) - control.y };
  });
  const minimumDeltaX = Math.max(...horizontalLimits.map((limit) => limit.minimum));
  const maximumDeltaX = Math.min(...horizontalLimits.map((limit) => limit.maximum));
  const minimumDeltaY = Math.max(...verticalLimits.map((limit) => limit.minimum));
  const maximumDeltaY = Math.min(...verticalLimits.map((limit) => limit.maximum));
  const resolveDelta = (offset, minimum, maximum) => (
    maximum >= offset
      ? offset
      : minimum <= -offset
        ? -offset
        : clamp(offset, minimum, maximum)
  );
  const deltaX = resolveDelta(offsetX, minimumDeltaX, maximumDeltaX);
  const deltaY = resolveDelta(offsetY, minimumDeltaY, maximumDeltaY);

  return controls.map((control) => ({
    ...control,
    x: control.x + deltaX,
    y: control.y + deltaY,
  }));
};

export const removeSelectedCanvasControls = (controls, selectedControlIds) => {
  const selectedIds = new Set(selectedControlIds);
  return controls.filter((control) => !selectedIds.has(control.id));
};

export const shouldRemoveSelectedCanvasControls = (clickedControlId, selectedControlIds) => (
  selectedControlIds.length > 1 && selectedControlIds.includes(clickedControlId)
);

export const moveSelectedCanvasControls = (controls, selectedControlIds, direction) => {
  if (!Array.isArray(controls) || !controls.length || !selectedControlIds?.length) return controls;
  if (direction !== "forward" && direction !== "backward") return controls;
  const selectedIds = new Set(selectedControlIds);
  const positionsBySurface = new Map();
  controls.forEach((control, index) => {
    const surface = control.parentPanelId ?? null;
    if (!positionsBySurface.has(surface)) positionsBySurface.set(surface, []);
    positionsBySurface.get(surface).push(index);
  });

  const next = controls.slice();
  let moved = false;
  positionsBySurface.forEach((positions) => {
    const items = positions.map((index) => next[index]);
    const selected = items.filter((item) => selectedIds.has(item.id));
    if (!selected.length || selected.length === items.length) return;
    const unselected = items.filter((item) => !selectedIds.has(item.id));
    const reordered = direction === "forward"
      ? [...unselected, ...selected]
      : [...selected, ...unselected];
    if (reordered.some((item, index) => item !== items[index])) moved = true;
    positions.forEach((position, index) => { next[position] = reordered[index]; });
  });
  return moved ? next : controls;
};

export const controlsOnSameInteractionSurface = (controls, selectedControlIds, activeControl) => {
  if (!activeControl) return [];
  const selectedIds = new Set(selectedControlIds);
  if (!selectedIds.has(activeControl.id) || selectedIds.size < 2) return [activeControl];
  const activeParentId = activeControl.parentPanelId ?? null;
  const matching = controls.filter((control) => (
    selectedIds.has(control.id) && (control.parentPanelId ?? null) === activeParentId
  ));
  return matching.length ? matching : [activeControl];
};
